/**
 * TripListPage — Liste publique des Carnets de voyage (statut published).
 * Route : /carnets-de-voyage
 */
import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { MapPin, Calendar, Ship, Map, Building, Compass, Car, Sparkles } from "lucide-react";
import { getTrips } from "../lib/api";
import { Skeleton } from "../components/ui/skeleton";

const EXPERIENCE_META = {
  all: { label: "Tous", icon: Sparkles },
  croisiere: { label: "Croisières", icon: Ship },
  sejour_club: { label: "Séjours & Clubs", icon: Building },
  circuit: { label: "Circuits", icon: Map },
  city_trip: { label: "City-trips", icon: Compass },
  road_trip: { label: "Road-trips", icon: Car },
  autre: { label: "Autres", icon: Sparkles },
};

export default function TripListPage() {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    getTrips()
      .then((res) => setTrips(res.data))
      .catch(() => setTrips([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () => (filter === "all" ? trips : trips.filter((t) => t.experience_type === filter)),
    [trips, filter]
  );

  return (
    <div className="min-h-screen bg-secondary pt-20" data-testid="trip-list-page">
      <section className="bg-white border-b border-border py-12 md:py-16">
        <div className="max-w-7xl mx-auto container-padding">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-primary mb-4">
            Carnets de voyage
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl">
            Nos expériences vécues, racontées jour après jour : croisières, séjours, circuits et découvertes.
          </p>

          {/* Filtres par type d'experience */}
          <div className="mt-8 flex flex-wrap gap-2" data-testid="trip-filters">
            {Object.entries(EXPERIENCE_META).map(([key, meta]) => {
              const Icon = meta.icon;
              const count = key === "all" ? trips.length : trips.filter((t) => t.experience_type === key).length;
              const active = filter === key;
              return (
                <button
                  key={key}
                  onClick={() => setFilter(key)}
                  disabled={key !== "all" && count === 0}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all border ${
                    active
                      ? "bg-primary text-white border-primary"
                      : count === 0
                      ? "bg-gray-50 text-gray-400 border-transparent cursor-not-allowed"
                      : "bg-white text-primary border-border hover:border-accent"
                  }`}
                  data-testid={`trip-filter-${key}`}
                >
                  <Icon className="h-4 w-4" />
                  {meta.label}
                  {count > 0 && <span className={`ml-1 text-xs ${active ? "text-white/80" : "text-muted-foreground"}`}>({count})</span>}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-padding">
        <div className="max-w-7xl mx-auto container-padding">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array(3).fill(0).map((_, i) => (
                <div key={i}>
                  <Skeleton className="aspect-[4/3] mb-4" />
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-full" />
                </div>
              ))}
            </div>
          ) : filtered.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
              {filtered.map((t) => {
                const meta = EXPERIENCE_META[t.experience_type] || EXPERIENCE_META.autre;
                const Icon = meta.icon;
                return (
                  <Link
                    key={t.id}
                    to={`/carnets-de-voyage/${t.slug}`}
                    className="group block bg-white rounded-lg overflow-hidden border border-border hover:shadow-xl transition-shadow"
                    data-testid={`trip-card-${t.slug}`}
                  >
                    <div className="aspect-[4/3] bg-primary/10 flex items-center justify-center relative">
                      {t.cover_media_url ? (
                        <img src={t.cover_media_url} alt={t.title} className="w-full h-full object-cover" />
                      ) : (
                        <Icon className="h-16 w-16 text-primary/30" />
                      )}
                      <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-1 bg-white/95 backdrop-blur rounded text-xs font-medium text-primary shadow">
                        <Icon className="h-3 w-3" />
                        {meta.label.replace(/s$/, "")}
                      </span>
                    </div>
                    <div className="p-6">
                      <h2 className="font-heading text-xl font-semibold text-primary group-hover:text-accent transition-colors line-clamp-2">
                        {t.title}
                      </h2>
                      {t.subtitle && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{t.subtitle}</p>}
                      <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
                        {t.start_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" />
                            {new Date(t.start_date).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}
                          </span>
                        )}
                        {t.destination_ids?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3.5 w-3.5" />
                            {t.destination_ids.length} étape{t.destination_ids.length > 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <Ship className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="font-heading text-2xl font-semibold text-primary mb-2">Bientôt en ligne</h3>
              <p className="text-muted-foreground">Nos premiers carnets arrivent très bientôt.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
